import * as THREE from 'three';
import { createFurniturePicker } from '../viewer/furniturePick.js';

/**
 * CG の家具に備品見積りの値段を出す。
 *
 * 家具には設計データ側で sku（見積りの品目名）を付けてある。
 * 家具をクリックすると、その品目の商品名・数量・単価・金額をカードで出す。
 *
 * 綿棒や洗剤のように形にできない小物は、居室ごとに「その他の備品」の札を
 * 部屋の中に浮かべ、それをクリックすると品目と金額の一覧が出る。
 *
 * 松・竹・梅のプランは品目の並びが同じで商品と金額だけが違うので、
 * パネル上部で切り替えると、札もカードも中身だけ差し替わる。
 */
const LABEL_LIFT = 1.25;

export function createPriceTags(container, { scene, root, camera, canvas, quote }) {
  const picker = createFurniturePicker({ root, camera, canvas });
  if (picker.items.size === 0) return null;

  let plan = quote.plans.find((p) => p.key === quote.defaultPlan) ?? quote.plans[0];
  let bySku = skuIndex(plan);

  const panel = document.createElement('aside');
  panel.className = 'quote-panel';
  panel.hidden = true;
  container.appendChild(panel);

  const card = document.createElement('div');
  card.className = 'quote-card';
  card.hidden = true;
  container.appendChild(card);

  // ---- 3D に浮かべる「その他の備品」の札 ----
  const tags = new THREE.Group();
  tags.name = 'quote-tags';
  tags.visible = false;
  scene.add(tags);
  const sprites = [];
  for (const [place, [x, y, z]] of Object.entries(quote.spots ?? {})) {
    const made = createTag();
    made.sprite.position.set(x, y + LABEL_LIFT, z);
    made.sprite.userData.place = place;
    tags.add(made.sprite);
    sprites.push({ place, ...made });
  }
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();

  /** 札の文字をいまのプランの金額で描き直す */
  const refreshTags = () => {
    for (const tag of sprites) {
      const rest = restOf(plan, tag.place);
      tag.sprite.visible = rest.items.length > 0;
      if (!tag.sprite.visible) continue;
      drawTag(tag, `その他の備品 ${rest.items.length}点`, yen(rest.total));
    }
  };

  const showCard = (html, event) => {
    card.innerHTML = html;
    const rect = container.getBoundingClientRect();
    // 画面の端で切れないよう、カードの幅ぶん内側に寄せる
    card.style.left = `${Math.min(Math.max(event.clientX - rect.left + 14, 12), rect.width - 290)}px`;
    card.style.top = `${Math.min(Math.max(event.clientY - rect.top + 14, 12), rect.height - 200)}px`;
    card.hidden = false;
  };

  /** 画面のクリック位置にある「その他の備品」の札 */
  const pickTag = (event) => {
    if (!tags.visible) return null;
    const rect = canvas.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hit = raycaster.intersectObjects(tags.children.filter((s) => s.visible), false)[0];
    return hit?.object.userData.place ?? null;
  };

  const onClick = (event) => {
    if (!enabled) return;
    const place = pickTag(event);
    if (place !== null) {
      event.preventDefault();
      event.stopPropagation();
      showCard(restCard(plan, place), event);
      return;
    }
    const node = picker.pick(event);
    if (node === null) {
      card.hidden = true;
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    showCard(itemCard(node.userData.furniture, bySku, quote, plan), event);
  };

  // クリックで視点が動かないよう、押した時点で先に止める
  const onPointerDown = (event) => {
    if (!enabled) return;
    if (pickTag(event) !== null || picker.pick(event) !== null) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  canvas.addEventListener('pointerdown', onPointerDown, { capture: true });
  canvas.addEventListener('click', onClick, { capture: true });

  const render = () => {
    panel.innerHTML = renderPanel(quote, plan, bySku, picker.items);
  };

  panel.addEventListener('click', (event) => {
    const tab = event.target.closest('.quote-tab');
    if (tab !== null) {
      plan = quote.plans.find((p) => p.key === tab.dataset.plan) ?? plan;
      bySku = skuIndex(plan);
      card.hidden = true;
      refreshTags();
      render();
      return;
    }
    const head = event.target.closest('.quote-room-head');
    if (head !== null) head.parentElement.classList.toggle('open');
  });

  let enabled = false;
  render();
  refreshTags();

  return {
    /** 値段モードの入・切 */
    setEnabled(next) {
      enabled = Boolean(next);
      panel.hidden = !enabled;
      tags.visible = enabled;
      if (!enabled) card.hidden = true;
      canvas.style.cursor = enabled ? 'help' : '';
    },
    get enabled() {
      return enabled;
    },
    /** いま選んでいるプラン */
    get plan() {
      return plan;
    },
    /** 札を画面の高さに対して一定の大きさに保つ（視野角が変わったとき） */
    updateTags(cam) {
      if (cam.fov === labelFov) return;
      labelFov = cam.fov;
      const height = 2 * 0.04 * Math.tan((cam.fov * Math.PI) / 360);
      for (const { sprite, aspect } of sprites) sprite.scale.set(height * aspect, height, 1);
    },
    dispose() {
      canvas.removeEventListener('pointerdown', onPointerDown, { capture: true });
      canvas.removeEventListener('click', onClick, { capture: true });
      for (const { texture } of sprites) texture.dispose();
      scene.remove(tags);
      panel.remove();
      card.remove();
    },
  };
}

let labelFov = null;

/** sku → 品目 */
function skuIndex(plan) {
  const map = new Map();
  for (const item of plan.items) if (item.sku) map.set(item.sku, item);
  return map;
}

/** その居室の、CG に形として置いていない品目 */
function restOf(plan, place) {
  const items = plan.items.filter((item) => item.place === place && item.sku === undefined && item.total !== undefined);
  return { items, total: items.reduce((a, b) => a + b.total, 0) };
}

/**
 * 家具ひとつぶんのカード。
 * いまのプランの商品と金額に加えて、他のプランならいくらかを並べる。
 * 「このソファは松ならいくらか」がその場で分かるようにするため。
 */
function itemCard({ sku, label }, bySku, quote, plan) {
  const item = sku === null ? undefined : bySku.get(sku);
  if (item === undefined) {
    return `<p class="quote-card-name">${escape(label)}</p><p class="quote-card-note">見積りに載っていない部材です（造作・工事側など）</p>`;
  }
  const others = quote.plans
    .map((p) => {
      const other = p.items.find((i) => i.sku === sku);
      const on = p.key === plan.key;
      return `<li${on ? ' class="is-on"' : ''}><span>${escape(p.name)}</span><span>${
        other?.total === undefined ? '—' : yen(other.total)
      }</span></li>`;
    })
    .join('');
  return [
    `<p class="quote-card-place">${escape(item.place)}</p>`,
    `<p class="quote-card-name">${escape(item.name)}</p>`,
    item.product ? `<p class="quote-card-product">${escape(item.product)}</p>` : '',
    `<p class="quote-card-price"><strong>${yen(item.total)}</strong>`,
    item.qty > 1 ? `<span>（${yen(item.unit)} × ${item.qty}）</span>` : '',
    '</p>',
    `<ul class="quote-compare">${others}</ul>`,
  ].join('');
}

/** 「その他の備品」のカード。形にできない小物をまとめて出す */
function restCard(plan, place) {
  const { items, total } = restOf(plan, place);
  const lines = items
    .map(
      (item) =>
        `<li><span>${escape(item.name)}${item.qty > 1 ? `<small> ×${item.qty}</small>` : ''}</span><span>${yen(
          item.total,
        )}</span></li>`,
    )
    .join('');
  return [
    `<p class="quote-card-place">${escape(place)}</p>`,
    `<p class="quote-card-name">その他の備品 ${items.length}点</p>`,
    `<p class="quote-card-price"><strong>${yen(total)}</strong></p>`,
    `<ul class="quote-card-list">${lines}</ul>`,
  ].join('');
}

/** 居室ごとにまとめた見積りのパネル */
function renderPanel(quote, plan, bySku, items) {
  const placed = new Set();
  for (const node of items.values()) {
    const { sku } = node.userData.furniture;
    if (sku !== null && bySku.has(sku)) placed.add(sku);
  }

  // タブには総額も出す。どのプランがいくらかを、切り替えずに見比べられるようにする
  const tabs = quote.plans
    .map(
      (p) =>
        `<button type="button" class="quote-tab${p.key === plan.key ? ' is-on' : ''}" data-plan="${p.key}">` +
        `<b>${escape(p.name)}</b><span>${man(p.total)}</span></button>`,
    )
    .join('');

  // 家具（CG に形がある物）と、小物（札にまとめた物）に分けて内訳を出す
  let furniture = 0;
  for (const item of plan.items) if (item.sku !== undefined && item.total !== undefined) furniture += item.total;
  const small = plan.total - furniture;

  const diffs = quote.plans
    .filter((p) => p.key !== plan.key)
    .map((p) => {
      const d = p.total - plan.total;
      return `${escape(p.name)}より <b>${yen(Math.abs(d))}</b> ${d > 0 ? '安い' : '高い'}`;
    })
    .join(' ／ ');

  const rooms = new Map();
  for (const item of plan.items) {
    if (!rooms.has(item.place)) rooms.set(item.place, []);
    rooms.get(item.place).push(item);
  }

  // 金額の大きい居室から並べ、棒の長さで大小が一目で分かるようにする
  const sorted = [...rooms]
    .map(([place, list]) => ({ place, list, sum: list.reduce((a, b) => a + (b.total ?? 0), 0) }))
    .sort((a, b) => b.sum - a.sum);
  const top = sorted[0]?.sum || 1;

  const line = (item) => {
    const shown = item.sku !== undefined && placed.has(item.sku);
    const price = item.total === undefined ? `<i>${escape(item.how)}</i>` : yen(item.total);
    return `<li${shown ? ' class="in-cg"' : ''}><span>${escape(item.name)}${
      item.qty > 1 ? `<small> ×${item.qty}</small>` : ''
    }</span><span>${price}</span></li>`;
  };

  const body = sorted
    .map(({ place, list, sum }) => {
      const inCg = list.filter((item) => item.sku !== undefined && placed.has(item.sku));
      const rest = list.filter((item) => !(item.sku !== undefined && placed.has(item.sku)));
      const groups = [
        inCg.length > 0 ? `<li class="quote-group">CG に置いてある家具</li>${inCg.map(line).join('')}` : '',
        rest.length > 0 ? `<li class="quote-group">その他の備品（部屋の札）</li>${rest.map(line).join('')}` : '',
      ].join('');
      return [
        '<section class="quote-room">',
        '<button type="button" class="quote-room-head">',
        `<span class="quote-room-bar" style="--w:${Math.round((sum / top) * 100)}%"></span>`,
        `<span class="quote-room-name">${escape(place)}</span><span>${yen(sum)}</span>`,
        `</button><ul>${groups}</ul></section>`,
      ].join('');
    })
    .join('');

  return [
    `<header><h2>${escape(quote.title)}</h2><div class="quote-tabs">${tabs}</div></header>`,
    `<p class="quote-total"><span>${escape(plan.label)}</span><strong>${yen(plan.total)}</strong></p>`,
    diffs === '' ? '' : `<p class="quote-diff">${diffs}</p>`,
    '<dl class="quote-split">',
    `<div><dt>CG に置いた家具</dt><dd>${yen(furniture)}</dd></div>`,
    `<div><dt>小物・消耗品</dt><dd>${yen(small)}</dd></div>`,
    '</dl>',
    '<p class="quote-help">3D の家具をクリックすると、その品目の値段と他プランの値段が出ます。',
    '形にできない小物は、部屋に浮かぶ札にまとめてあります。</p>',
    '<p class="quote-label">居室べつ（金額の大きい順）</p>',
    `<div class="quote-rooms">${body}</div>`,
    `<p class="quote-source">出典: ${escape(quote.source)}<br>${escape(quote.note)}</p>`,
  ].join('');
}

/** ざっくりの万円表記。タブなど狭い所で使う */
const man = (n) => `${(Math.round(n / 1000) / 10).toLocaleString('ja-JP')}万`;

/** 3D に浮かべる札。遠くても同じ大きさで読めるようにする */
function createTag() {
  const canvas = document.createElement('canvas');
  canvas.width = 420;
  canvas.height = 108;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, depthTest: false, depthWrite: false, sizeAttenuation: false }),
  );
  sprite.renderOrder = 12;
  sprite.scale.set(0.09 * (canvas.width / canvas.height), 0.09, 1);
  return { sprite, texture, canvas, aspect: canvas.width / canvas.height };
}

function drawTag({ canvas, texture }, title, price) {
  const c = canvas.getContext('2d');
  c.clearRect(0, 0, canvas.width, canvas.height);
  c.fillStyle = 'rgba(13, 17, 23, 0.86)';
  roundRect(c, 0, 0, canvas.width, canvas.height, 22);
  c.fill();
  c.strokeStyle = 'rgba(34, 211, 238, 0.85)';
  c.lineWidth = 3;
  roundRect(c, 1.5, 1.5, canvas.width - 3, canvas.height - 3, 21);
  c.stroke();
  c.fillStyle = '#9aa7b6';
  c.font = '26px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
  c.textBaseline = 'middle';
  c.fillText(title, 22, 34);
  c.fillStyle = '#22d3ee';
  c.font = 'bold 38px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
  c.fillText(price, 22, 76);
  texture.needsUpdate = true;
}

function roundRect(c, x, y, w, h, r) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

const yen = (n) => `${Number(n).toLocaleString('ja-JP')}円`;

const escape = (s) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
